/**
 * CustomerFilterContext — الشريط المشترك لصفحة العملاء (تاريخ + فرع).
 *
 * Provider واحد أعلى مركز العملاء يحمل نافذة زمنية (يومي/أسبوعي/شهري +
 * تاريخ يدوي بنمط RepeatRateCard) + فرعًا مختارًا. البطاقات السبع الجاهزة
 * تشترك فيه بدل فلاترها المحلية.
 *
 * سلوك التجاوز المحلي:
 *   - كل تغيير في الشريط المشترك يرفع `version` → البطاقات تُصفّر تجاوزها
 *     المحلي وتتبع الشريط.
 *   - تغيير محلي على بطاقة معيّنة يبقى ساريًا لتلك البطاقة فقط حتى أول
 *     تغيير تالٍ بالشريط.
 *
 * لا فلتر قناة (رتم/تكر) — مؤجَّل صراحة.
 */
import React, { createContext, useContext, useEffect, useMemo, useState, useRef } from "react";
import { api } from "../lib/api";
import { resolvePreset } from "../DateRangePicker";
import { GRAIN_PRESET, resolveGrainWindow } from "./grainPreset";
import DateField from "../components/DateField";
import BranchSelect from "./BranchSelect";

const TEAL = "#065F46";
const TEAL_SOFT = "#0F766E";
const BORDER = "1px solid rgba(6,95,70,0.20)";

const GRAINS = [
  { id: "day", label: "يومي" },
  { id: "week", label: "أسبوعي" },
  { id: "month", label: "شهري" },
];

const CustomerFilterCtx = createContext(null);

// Hook — يُعيد null إن لم يُلَف المكوّن بالـProvider (توافق مع الاختبارات
// المعزولة والاستخدام القديم للبطاقات خارج المركز).
export function useCustomerFilter() {
  return useContext(CustomerFilterCtx);
}

export function CustomerFilterProvider({ children, initialGrain = "week" }) {
  const [grain, setGrain] = useState(initialGrain);
  const [manualStart, setManualStart] = useState("");
  const [manualEnd, setManualEnd] = useState("");
  const [branchId, setBranchId] = useState("");
  const [branches, setBranches] = useState([]);
  const [version, setVersion] = useState(0);
  // 2026-10-11 (جولة إصلاح — البند 3): يوم بداية الأسبوع من إعدادات
  // المستأجر (week_start_day عبر command-room/settings/tenant)، لا Sunday
  // مُثبَّتة. Python weekday() في الباك-إند (الاثنين=0) ⇐ JS getDay() (الأحد=0):
  // jsIdx = (pythonIdx + 1) % 7.
  const [weekStartIdx, setWeekStartIdx] = useState(0);
  useEffect(() => {
    let cancelled = false;
    api.get("/command-room/settings/tenant")
      .then((r) => {
        if (cancelled) return;
        const name = (r.data?.settings?.week_start_day || "sunday").toLowerCase();
        const pyIdx = { monday: 0, tuesday: 1, wednesday: 2, thursday: 3,
                         friday: 4, saturday: 5, sunday: 6 }[name];
        if (pyIdx !== undefined) setWeekStartIdx((pyIdx + 1) % 7);
      })
      .catch(() => { /* يبقى الافتراضي Sunday عند أي عطل */ });
    return () => { cancelled = true; };
  }, []);

  const manualActive = Boolean(manualStart || manualEnd);
  const preset = resolvePreset(GRAIN_PRESET[grain], weekStartIdx) || { from: "", to: "" };
  const win = resolveGrainWindow(preset, manualStart, manualEnd);
  const from = win.from;
  const to = win.to;

  // كل تغيير فعلي في نافذة/فرع الشريط يرفع version (يُصفّر تجاوز البطاقات).
  const firstRun = useRef(true);
  useEffect(() => {
    if (firstRun.current) { firstRun.current = false; return; }
    setVersion((v) => v + 1);
  }, [from, to, branchId]);

  // قائمة الفروع للشريط المشترك (مصدر واحد — نفس شكل بقية البطاقات).
  useEffect(() => {
    let cancelled = false;
    api.get("/customers/repeat-rate", { params: {} })
      .then((r) => { if (!cancelled) setBranches(r.data?.branches || []); })
      .catch(() => { /* الفروع اختيارية للشريط */ });
    return () => { cancelled = true; };
  }, []);

  const value = useMemo(() => ({
    grain, setGrain,
    manualStart, setManualStart,
    manualEnd, setManualEnd,
    manualActive,
    branchId, setBranchId,
    branches,
    from, to,
    version,
  }), [grain, manualStart, manualEnd, manualActive, branchId, branches, from, to, version]);

  return (
    <CustomerFilterCtx.Provider value={value}>
      {children}
    </CustomerFilterCtx.Provider>
  );
}

const _lbl = { fontSize: 10, color: TEAL_SOFT, fontWeight: 700 };
const _input = {
  fontSize: 13, padding: "6px 10px", borderRadius: 10,
  border: BORDER, background: "#fff", color: TEAL, fontWeight: 600,
};

// شريط الفلترة المستقل — نسخة لكل تبويب (Provider مستقل لكل تبويب).
// showBranch=false يُستخدم في تبويب «تحليلات الشرائح» لأن نقطة الباك-إند
// team-summary لا تدعم branch_id (routes/customer_metrics.py:177).
export function CustomerFilterBar({ showBranch = true }) {
  const f = useCustomerFilter();
  if (!f) return null;
  return (
    <section
      data-testid="customer-filter-bar"
      dir="rtl"
      style={{
        marginBottom: 20, padding: "16px 20px", borderRadius: 16,
        background: "#fff", border: BORDER,
        boxShadow: "0 1px 3px rgba(6,95,70,0.06)",
        display: "flex", gap: 16, flexWrap: "wrap", alignItems: "flex-end",
        fontVariantNumeric: "lining-nums tabular-nums",
      }}
    >
      <div style={{ display: "grid", gap: 4 }}>
        <span style={_lbl}>🔗 فلتر التبويب — الفترة (يوم تشغيلي 7ص → 6:59ص)</span>
        <div data-testid="shared-filter-grain"
             style={{ display: "flex", background: "rgba(6,95,70,0.08)", borderRadius: 10, padding: 2 }}>
          {GRAINS.map((g) => {
            const active = f.grain === g.id && !f.manualActive;
            return (
              <button key={g.id}
                      data-testid={`shared-filter-grain-${g.id}`}
                      onClick={() => { f.setManualStart(""); f.setManualEnd(""); f.setGrain(g.id); }}
                      style={{
                        padding: "6px 16px", borderRadius: 8, border: "none",
                        cursor: "pointer", fontSize: 13, fontWeight: active ? 800 : 600,
                        background: active ? "#fff" : "transparent",
                        color: active ? TEAL : TEAL_SOFT,
                        boxShadow: active ? "0 1px 3px rgba(6,95,70,0.15)" : "none",
                        transition: "background 0.15s ease",
                      }}>
                {g.label}
              </button>
            );
          })}
        </div>
      </div>
      <label style={{ display: "grid", gap: 4 }}>
        <span style={_lbl}>من تاريخ (يدوي)</span>
        <DateField data-testid="shared-filter-start" value={f.manualStart}
          onChange={(e) => f.setManualStart(e.target.value)}
          style={{ ..._input, width: 160 }} />
      </label>
      <label style={{ display: "grid", gap: 4 }}>
        <span style={_lbl}>إلى تاريخ (يدوي)</span>
        <DateField data-testid="shared-filter-end" value={f.manualEnd}
          onChange={(e) => f.setManualEnd(e.target.value)}
          style={{ ..._input, width: 160 }} />
      </label>
      {showBranch && (
        <BranchSelect
          value={f.branchId}
          onChange={f.setBranchId}
          branches={f.branches}
          testId="shared-filter-branch"
          label="الفرع"
        />
      )}
      <div data-testid="shared-filter-window-label"
           style={{ marginInlineStart: "auto", fontSize: 11, color: TEAL_SOFT, fontWeight: 600 }}>
        النافذة الحالية: {f.from || "—"} ← {f.to || "—"}
      </div>
    </section>
  );
}
